import moment from "moment-timezone";

import { getPeriodDateRange } from "./dateRangeHelper";
import {
  capitalizeFirstLetter,
  DEFAULT_DATE_FORMAT,
  TIME_ZONE,
  TimesheetStatus,
} from "./payRequestConstants";

/*
 * Transliteration of the mobile pay-request rate engine,
 * procare_mobile_app/src/components/payRequestForm/index.ts.
 *
 * Kept LINE-FOR-LINE identical to that source so a rate change made on mobile
 * can be applied here by line number: lines 28-871 below are mobile lines
 * 28-871, and lines 872-878 are mobile lines 1166-1173.
 *
 * Only five things may differ, and nothing else should ever be "cleaned up" -
 * the oddities below are the parity contract, not bugs to fix:
 *   1. TypeScript annotations are stripped.
 *   2. The imports above replace mobile's (which imports plain `moment` and
 *      relies on dateRangeHelper patching it with moment-timezone).
 *   3. Mobile's four React Native barrel re-exports are not copied.
 *   4. budgetComparisons / calculatePeriodBudgets (mobile 872-1165) is not
 *      ported, so the return has two keys instead of three.
 *   5. Model C/D's step/multisite hours-style branch prices a row entirely at
 *      the tier its cumulative total lands in (cliff-style), instead of
 *      mobile's blended per-tier split. A row that straddles a tier boundary
 *      was showing an Amount that didn't equal Quantity x its own displayed
 *      Rate - PPMA-418. If mobile is fixed the same way, this deviation can
 *      be removed.
 */
const getUnitLabel = (rateUnitType) => {
  if (rateUnitType === "hourly") return "hr";
  if (rateUnitType === "daily") return "day";
  return rateUnitType;
};

/*
 * Shift-threshold support, mirrored from the mobile engine's shift-unit
 * additions: a shift-threshold contract's "quantity" is the decimal shifts
 * value on the row/timesheet rather than hours + minutes.
 */
const isShiftUnit = (unitType) => String(unitType ?? "").toLowerCase() === "shift";

const isCountUnit = (unitType) => !["hours", "minutes", "days"].includes(String(unitType ?? "").toLowerCase());

// Count-style units (shift or any non-hours/minutes/days unit) are entered as a
// quantity in `shifts` and tiered through the same cumulative path as hours.
const isQuantityUnit = (unitType) => isShiftUnit(unitType) || isCountUnit(unitType);

const isHoursStyleUnit = (unitType) => String(unitType ?? "").toLowerCase() === "hours" || isQuantityUnit(unitType);

const getEntryQuantity = (entry, unitType) => {
  if (isQuantityUnit(unitType)) {
    return Number(Number(entry.shifts ?? 0).toFixed(2));
  }
  return Number(entry.hours) + Number(entry.minutes) / 60;
};

const getRateUnitSuffix = (unitType) => (isQuantityUnit(unitType) ? "/shift" : "/hr");

function calculateBalance(
  firstContract,
  totalMinutes,
) {
  if (firstContract.remaining_period_cap !== null) {
    return `${(((firstContract.remaining_period_cap ?? 0) * 60 - totalMinutes) / 60)?.toFixed(2)}h`;
  }
  if (firstContract.remaining_annual_cap !== null) {
    return `${(((firstContract.remaining_annual_cap ?? 0) * 60 - totalMinutes) / 60)?.toFixed(2)}h`;
  }
  return null;
}

function calculateSoftFlag(
  firstContract,
  balance,
) {
  if (firstContract.remaining_period_cap === null) {
    return false;
  }
  if (balance !== null) {
    return Number(balance.substring(0, balance.length - 1)) < 0;
  }
  return false;
}

function calculateHardFlag(
  firstContract,
  balance,
) {
  if (firstContract.remaining_annual_cap === null) {
    return false;
  }
  if (balance !== null) {
    return Number(balance.substring(0, balance.length - 1)) < 0;
  }
  return false;
}

function calculateTierBalance(
  tier,
  totalMinutes,
) {
  if (!tier) return null;
  if (tier.remaining_period_cap !== null) {
    return `${(((tier.remaining_period_cap ?? 0) * 60 - totalMinutes) / 60)?.toFixed(2)}h`;
  }
  if (tier.remaining_annual_cap !== null) {
    return `${(((tier.remaining_annual_cap ?? 0) * 60 - totalMinutes) / 60)?.toFixed(2)}h`;
  }
  return null;
}

function calculateTierHardFlag(
  tier,
  balance,
) {
  if (!tier) return false;
  if (tier.remaining_annual_cap === null) {
    return false;
  }
  if (balance !== null) {
    return Number(balance.substring(0, balance.length - 1)) < 0;
  }
  return false;
}

// root calculation function.

export const calcuateAdvancedRate = async (
  contractProvider,
  rows,
  selectedContractMstrKey,
  payRequestName,
  shiftType,
  allTimesheetsList = [],
) => {
  /*
   * Returns { rowsData, multiTierStr }. Mobile's third key, budgetComparisons,
   * is deliberately not ported here - see the note at the end of the file.
   */

  const rowData = [...rows];
  // (mobile declares `budgetComparisons` here; not ported)

  const contractData = contractProvider ?? [];
  const firstContract = contractData.length > 0 ? contractData[0] : null;

  const isMultisiteByContractName =
    firstContract?.rate_type === "multisite-day" ||
    firstContract?.rate_type === "multisite-periodend";

  const timesheetListData = allTimesheetsList
    .filter(
      it =>
        it.status.timesheet_status_mstr_key !==
        TimesheetStatus.REJECTED.mstr_key,
    )
    .filter(it => {
      if (firstContract?.combined_threshold_id !== null) {
        return (
          it.combined_threshold_id === firstContract?.combined_threshold_id
        );
      }
      if (isMultisiteByContractName) {
        const contractNameKey = payRequestName.id ?? null;
        const shiftTypeKey = shiftType.id ?? null;
        if (contractNameKey !== null) {
          return (
            it.contract_name_mstr_key === contractNameKey &&
            (shiftTypeKey == null || it.shift_type_mstr_key === shiftTypeKey)
          );
        }
        const contractName =
          payRequestName.title ?? payRequestName.contract_mstr_key;
        return (
          it.contract_name.toString() === contractName &&
          (shiftTypeKey == null || it.shift_type_mstr_key === shiftTypeKey)
        );
      }

      return (
        it.contract_mstr_key === firstContract.contract_mstr_key &&
        it.location_mstr_key === firstContract.location_mstr_key
      );
    });

  // const timesheetListData = allTimesheetsList
  //   .filter(
  //     it =>
  //       it.status.timesheet_status_mstr_key !==
  //       TimesheetStatus.REJECTED.mstr_key,
  //   )
  //   .filter(
  //     it => firstContract?.combined_threshold_id === it.combined_threshold_id,
  //   )
  //   .filter(
  //     it =>
  //       isMultisiteByContractName &&
  //       it.contract_name.toString() === payRequestNameStr,
  //   );

  if (!contractProvider)
    return { rowsData: rows, multiTierStr: "" };

  let multiTierStr = "";

  // FIRST WE WILL CREATE THE RATE STR FOR THE MULTI TIERS DATA...
  if (
    firstContract?.rate_type === "multisite" ||
    firstContract?.rate_type === "step" ||
    firstContract?.rate_type === "stemi" ||
    firstContract?.rate_type === "gen-cardio" ||
    firstContract?.rate_type === "multisite-multiregion-step" ||
    firstContract?.rate_type === "multisite-multiregion-flat" ||
    firstContract?.rate_type === "multisite-day" ||
    firstContract?.rate_type === "multisite-periodend" ||
    firstContract?.rate_type === "weekend-pay"
  ) {
    multiTierStr = (contractData ?? [])
      .map(tier => {
        const min = tier.min_thresold || 0;
        const max = tier.max_thresold ? `-${tier.max_thresold}` : "+";

        if (isQuantityUnit(tier.thresold_unit_type)) {
          return `${capitalizeFirstLetter(tier.thresold_unit_type)} ${min}${max}: $${tier.rate?.toFixed(2)}/${tier.thresold_unit_type}`;
        }

        const range = `${tier.thresold_unit_type === "days" ? "Days" : "Hours"} ${min}${max}`;

        let unit = getUnitLabel(tier.rate_unit_type);

        let rate =
          tier.rate_unit_type === "daily"
            ? (Number(tier.rate) * 24)?.toFixed(2)
            : tier.rate?.toFixed(2);
        return `${range}: $${rate}/${unit}`;
      })
      .join("\n");
  }

  if (!firstContract) {
    return { rowsData: rows, multiTierStr };
  }

  rowData.forEach(row => {
    const periodType = firstContract?.period_type || "monthly";
    const contractStartDate = firstContract?.contract_start_date || null;
    const periodRange = getPeriodDateRange(
      moment.tz(row.date, moment.tz.guess()).toDate(),
      //moment(row.date, "YYYY-MM-DD").toDate(),
      periodType,
      contractStartDate,
    );
    const periodStartDate = periodRange.startDate;
    const periodEndDate = periodRange.endDate;

    const periodTimesheets = timesheetListData?.filter(ts => {
      const time = moment.tz(ts.ts_date, TIME_ZONE);
      const tsDate = time.clone().tz(moment.tz.guess()).toDate();

      return (tsDate >= periodStartDate && tsDate <= periodEndDate) ?? [];
    });

    const totalMinutes = getEntryQuantity(row, firstContract.thresold_unit_type) * 60;
    let applicableTier = null;
    let dayIndex = null;

    // LET MODEL A: Is transactional 0 and rate type is flat
    if (
      firstContract?.rate_type === "flat" &&
      firstContract.is_transactional === 0
    ) {
      // Check if period fee already charged
      const hasPeriodFee = periodTimesheets.some(ts => ts.budget > 0);
      if (hasPeriodFee) {
        row.amount = 0;
        row.rate = 0;
      } else {
        const currentPeriodMinutes = periodTimesheets.reduce(
          (sum, ts) =>
            sum + getEntryQuantity(ts, firstContract.thresold_unit_type) * 60,
          0,
        );
        const totalPeriodMinutes = currentPeriodMinutes + totalMinutes;
        if (totalPeriodMinutes >= firstContract.min_thresold * 60) {
          row.rate = firstContract.rate;
          row.amount = firstContract.rate; // Single period fee
        } else {
          row.rate = 0;
          row.amount = 0;
        }

        row.balanceTitle = `Balance: (${capitalizeFirstLetter(firstContract?.period_type ?? "")})`;
        const balance = calculateBalance(firstContract, totalMinutes);
        row.softFlag = calculateSoftFlag(firstContract, balance);
        row.hardFlag = calculateHardFlag(firstContract, balance);
        row.maxHours = firstContract.max_thresold
          ? `${firstContract.max_thresold}h`
          : "";
        row.annualCap = firstContract.annual_cap
          ? `${firstContract.annual_cap}h`
          : "";
        const periodType = firstContract?.period_type || "";
        const formattedPeriod = periodType.endsWith("ly")
          ? periodType.slice(0, -2)
          : periodType;
        row.rateStr = `Rate: $${firstContract.rate?.toFixed(2)}/${formattedPeriod}`;

        // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
        timesheetListData.push({
          ...periodTimesheets[0],
          contract_mstr_key: selectedContractMstrKey,
          budget: row.amount,
          ts_date: row.date,
          hours: Number(row.hours),
          minutes: Number(row.minutes),
          shifts: row.shifts,
          amount: row.amount,
        });
      }
    }

    // // Model A2: Is Transactional 0 and rate type is step or multisite
    else if (
      firstContract.is_transactional === 0 &&
      (firstContract.rate_type === "step" ||
        firstContract.rate_type === "multisite")
    ) {
      let contractTiers = contractData.filter(
        item =>
          item.contract_mstr_key === firstContract.contract_mstr_key &&
          item.contract_name_mstr_key === firstContract.contract_name_mstr_key,
      );
      let hasTierFee = false;
      if (firstContract.thresold_unit_type === "days") {
        // Group timesheets by date to find distinct work dates
        const dateGroups = {};
        periodTimesheets.forEach(ts => {
          const dateKey = moment
            .tz(ts.ts_date, DEFAULT_DATE_FORMAT, TIME_ZONE)
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD");
          if (!dateGroups[dateKey]) {
            dateGroups[dateKey] = [];
          }
          dateGroups[dateKey].push(ts);
        });
        // Add the current service date to the groups
        const serviceDateStr = moment(row.date).format("YYYY-MM-DD");
        if (!dateGroups[serviceDateStr]) {
          dateGroups[serviceDateStr] = [];
        }
        // Sort distinct dates chronologically to assign day_index
        const distinctDates = Object.keys(dateGroups).sort();
        const dayIndexMap = {};
        distinctDates.forEach((date, index) => {
          dayIndexMap[date] = index + 1; // 1-based indexing
        });

        dayIndex = dayIndexMap[serviceDateStr];

        // Find applicable tier based on day index
        applicableTier = null;
        for (const tier of contractTiers) {
          const minThreshold = tier.min_thresold || 0;
          const maxThreshold = tier.max_thresold || Infinity;

          if (dayIndex >= minThreshold && dayIndex <= maxThreshold) {
            applicableTier = tier;
            break;
          }
        }
        // If no tier found, use the last tier (highest range)
        if (!applicableTier && contractTiers.length > 0) {
          applicableTier = contractTiers[contractTiers.length - 1];
        }
        // Check if tier fee already charged for this tier
        // Find all timesheets in the same tier and check if any has budget > 0
        if (applicableTier) {
          hasTierFee = periodTimesheets.some(ts => {
            const tsDateKey = moment
              .tz(ts.ts_date, TIME_ZONE)
              .clone()
              .tz(moment.tz.guess())
              .format("YYYY-MM-DD");
            const tsDayIndex = dayIndexMap[tsDateKey];

            // Check if this timesheet falls in the same tier
            const minThreshold = applicableTier?.min_thresold || 0;
            const maxThreshold = applicableTier?.max_thresold || Infinity;
            const isInSameTier =
              tsDayIndex >= minThreshold && tsDayIndex <= maxThreshold;

            return isInSameTier && ts.budget > 0;
          });
        }
      } else if (isHoursStyleUnit(firstContract.thresold_unit_type)) {
        // Hours-based tier determination

        // Sort tiers by min_thresold ascending
        const sortedTiers = [...contractTiers].sort(
          (a, b) => (a.min_thresold || 0) - (b.min_thresold || 0),
        );

        // Calculate cumulative hours before this entry
        const sortedTimesheets = periodTimesheets
          .filter(ts => {
            const tsDate = moment
              .tz(ts.ts_date, TIME_ZONE)
              .clone()
              .tz(moment.tz.guess())
              .toDate();
            return tsDate <= moment(row.date).toDate();
          })
          .sort(
            (a, b) =>
              new Date(a.ts_date).getTime() - new Date(b.ts_date).getTime(),
          );
        let cumulativeMinutes = sortedTimesheets.reduce(
          (sum, ts) =>
            sum + getEntryQuantity(ts, firstContract.thresold_unit_type) * 60,
          0,
        );
        let finalCumulativeMinutes = cumulativeMinutes + totalMinutes;

        // Find applicable tier: highest tier where final cumulative > min_thresold
        applicableTier = null;
        for (const tier of sortedTiers) {
          if (finalCumulativeMinutes > (tier.min_thresold * 60 || 0)) {
            applicableTier = tier;
          }
        }
        // Check if tier fee already charged for this tier
        // For each existing timesheet, recalculate its cumulative position and determine its tier
        if (applicableTier) {
          let runningMinutes = 0;
          const timesheetsWithTiers = periodTimesheets
            .sort(
              (a, b) =>
                new Date(a.ts_date).getTime() - new Date(b.ts_date).getTime(),
            )
            .map(ts => {
              const tsMinutes =
                getEntryQuantity(ts, firstContract.thresold_unit_type) * 60;
              runningMinutes += tsMinutes;

              // Find tier for this timesheet based on its cumulative position
              let tsTier = null;
              for (const tier of sortedTiers) {
                if (runningMinutes > (tier.min_thresold * 60 || 0)) {
                  tsTier = tier;
                }
              }

              return { ...ts, tier: tsTier };
            });

          // Check if any timesheet in the same tier has budget > 0
          hasTierFee = timesheetsWithTiers.some(ts => {
            const isInSameTier =
              ts.tier &&
              applicableTier &&
              ts.tier.min_thresold === applicableTier.min_thresold &&
              ts.tier.max_thresold === applicableTier.max_thresold;

            return isInSameTier && ts.budget > 0;
          });
        }
      }
      if (!hasTierFee && applicableTier) {
        row.rate = applicableTier.rate || 0;
        row.amount = row.rate; // One-time tier fee
      } else {
        row.rate = 0;
        row.amount = 0;
      }
      row.balanceTitle = `Balance: (${capitalizeFirstLetter(firstContract?.period_type ?? "")})`;
      const balance = calculateBalance(firstContract, totalMinutes);
      row.softFlag = calculateSoftFlag(firstContract, balance);
      row.hardFlag = calculateHardFlag(firstContract, balance);
      row.maxHours = firstContract.max_thresold
        ? `${firstContract.max_thresold}h`
        : "";
      row.annualCap = firstContract.annual_cap
        ? `${firstContract.annual_cap}h`
        : "";
      const periodType = firstContract?.period_type || "";
      const formattedPeriod = periodType.endsWith("ly")
        ? periodType.slice(0, -2)
        : periodType;
      row.rateStr = `Rate: $${firstContract.rate?.toFixed(2)}/${formattedPeriod}`;
      // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
      timesheetListData.push({
        ...periodTimesheets[0],
        contract_mstr_key: selectedContractMstrKey,
        budget: row.amount,
        ts_date: row.date,
        hours: Number(row.hours),
        minutes: Number(row.minutes),
        shifts: row.shifts,
        amount: row.amount,
      });
    }

    // MODEL B:  Is transaction 1 and rate type is flat
    else if (
      firstContract.is_transactional === 1 &&
      (firstContract.rate_type === "flat" ||
        firstContract.rate_type === "multisite-multiregion-flat")
    ) {
      row.rate = firstContract.rate || 0;
      row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));

      // Apply caps if present
      if (
        firstContract.annual_comp_cap &&
        row.amount > firstContract.annual_comp_cap
      ) {
        row.amount = Number(firstContract.annual_comp_cap?.toFixed(2));
      }

      row.balanceTitle = `Balance: (${capitalizeFirstLetter(firstContract?.period_type ?? "")}`;
      const balance = calculateBalance(firstContract, totalMinutes);
      row.softFlag = calculateSoftFlag(firstContract, balance);
      row.hardFlag = calculateHardFlag(firstContract, balance);
      row.maxHours = firstContract.max_thresold
        ? `${firstContract.max_thresold}h`
        : "";
      row.annualCap =
        firstContract.annual_cap !== null ? `${firstContract.annual_cap}h` : "";

      row.rateStr = `Rate: $${firstContract.rate?.toFixed(2)}${getRateUnitSuffix(firstContract.thresold_unit_type)}`;

      // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
      timesheetListData.push({
        ...periodTimesheets[0],
        contract_mstr_key: selectedContractMstrKey,
        budget: row.amount,
        ts_date: row.date,
        hours: Number(row.hours),
        minutes: Number(row.minutes),
        shifts: row.shifts,
        amount: row.amount,
      });
    }

    // Model C & D: is_transactional = 1, rate_type = "step" or "cliff" "multisite-day", or "multisite-periodend" (tiered)
    else if (
      firstContract.is_transactional === 1 &&
      (firstContract.rate_type === "step" ||
        firstContract.rate_type === "multisite-multiregion-step" ||
        firstContract.rate_type === "stemi" ||
        firstContract.rate_type === "gen-cardio" ||
        firstContract.rate_type === "cliff" ||
        firstContract.rate_type === "multisite" ||
        firstContract.rate_type === "multisite-day" ||
        firstContract.rate_type === "multisite-periodend")
    ) {
      if (firstContract.thresold_unit_type === "days") {
        const dateGroups = {};
        periodTimesheets.forEach(ts => {
          const dateKey = moment
            .tz(ts.ts_date, TIME_ZONE)
            .clone()
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD");
          if (!dateGroups[dateKey]) {
            dateGroups[dateKey] = [];
          }
          dateGroups[dateKey].push(ts);
        });
        //Add the current service date
        const serviceDateStr = moment(row.date).format("YYYY-MM-DD");
        if (!dateGroups[serviceDateStr]) {
          dateGroups[serviceDateStr] = [];
        }
        const distinctDates = Object.keys(dateGroups).sort();
        const dayIndexMap = {};
        distinctDates.forEach((dateStr, index) => {
          dayIndexMap[dateStr] = index + 1; // 1-based indexing
        });
        dayIndex = dayIndexMap[serviceDateStr];
        applicableTier = null;
        for (const tier of contractData) {
          const minThreshold = tier.min_thresold || 0;
          const maxThreshold = tier.max_thresold || Infinity;

          if (dayIndex >= minThreshold && dayIndex <= maxThreshold) {
            applicableTier = tier;
            break;
          }
        }
        // If no tier found, use the last tier (highest range)
        if (!applicableTier && contractData.length > 0) {
          applicableTier = contractData[contractData.length - 1];
        }
        row.rate = applicableTier?.rate ?? 0;
        row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));
        row.balanceTitle = "Balance";
        row.balance = calculateTierBalance(
          applicableTier,
          totalMinutes,
        )?.toFixed();
        row.maxHours = null;
        row.annualCap = applicableTier?.annual_cap
          ? `${applicableTier?.annual_cap}h`
          : "";
        row.softFlag = false;
        row.rateStr = `Rate: $ ${applicableTier?.rate?.toFixed(2)}${getRateUnitSuffix(firstContract.thresold_unit_type)}`;
        row.hardFlag =
          applicableTier?.remaining_annual_cap === null
            ? false
            : (applicableTier?.remaining_annual_cap ?? 0) <= 0;
        // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
        timesheetListData.push({
          ...periodTimesheets[0],
          contract_mstr_key: selectedContractMstrKey,
          budget: row.amount,
          ts_date: row.date,
          hours: Number(row.hours),
          minutes: Number(row.minutes),
          shifts: row.shifts,
          amount: row.amount,
        });
      } else if (isHoursStyleUnit(firstContract.thresold_unit_type)) {
        const sortedTimesheets = periodTimesheets
          .filter(ts => {
            const tsDate = moment
              .tz(ts.ts_date, TIME_ZONE)
              .clone()
              .tz(moment.tz.guess())
              .toDate();
            return tsDate <= moment(row.date).toDate();
          })
          .sort(
            (a, b) =>
              new Date(a.ts_date).getTime() - new Date(b.ts_date).getTime(),
          );
        let cumulativeMinutes = sortedTimesheets.reduce(
          (sum, ts) =>
            sum + getEntryQuantity(ts, firstContract.thresold_unit_type) * 60,
          0,
        );
        if (
          firstContract.rate_type === "step" ||
          firstContract.rate_type === "multisite-multiregion-step" ||
          firstContract.rate_type === "multisite" ||
          firstContract.rate_type === "multisite-day" ||
          firstContract.rate_type === "multisite-periodend"
        ) {
          const sortedTiers = contractData.sort(
            (a, b) => (a.min_thresold || 0) - (b.min_thresold || 0),
          );

          // Calculate final cumulative position after this entry
          const finalCumulativeMinutes = cumulativeMinutes + totalMinutes;

          // The whole row is priced at the single tier its cumulative total
          // lands in - not blended/split across tiers within one row. A row
          // whose cumulative total crosses a tier boundary is billed entirely
          // at the tier it ends in, same as the cliff branch below.
          for (const tier of sortedTiers) {
            if (finalCumulativeMinutes > (tier.min_thresold * 60 || 0)) {
              applicableTier = tier;
            }
          }

          const hourlyRate = applicableTier?.rate || 0;
          row.rate = hourlyRate;
          row.amount = Number((totalMinutes * (hourlyRate / 60))?.toFixed(2));
          row.softFlag = false;
          row.balanceTitle = "Balance";
          row.rateStr = `Rate: $ ${applicableTier?.rate?.toFixed(2)}${getRateUnitSuffix(firstContract.thresold_unit_type)}`;
          row.balance = calculateTierBalance(applicableTier, totalMinutes);
          row.hardFlag = calculateTierHardFlag(applicableTier, row.balance);
          row.maxHours = null;
          row.annualCap = applicableTier?.annual_cap
            ? `${applicableTier?.annual_cap}h`
            : "";
          // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
          timesheetListData.push({
            ...periodTimesheets[0],
            contract_mstr_key: selectedContractMstrKey,
            budget: row.amount,
            ts_date: row.date,
            hours: Number(row.hours),
            minutes: Number(row.minutes),
            shifts: row.shifts,
            amount: row.amount,
          });
        } else if (firstContract.rate_type === "cliff") {
          let currentTier = null;

          const sortedTiers = contractData.sort(
            (a, b) => (a.min_thresold || 0) - (b.min_thresold || 0),
          );

          const finalCumulativeMinutes = cumulativeMinutes + totalMinutes;

          for (const tier of sortedTiers) {
            if (finalCumulativeMinutes > (tier.min_thresold * 60 || 0)) {
              currentTier = tier;
            }
          }
          applicableTier = currentTier;
          row.rate = currentTier ? currentTier.rate / 60 : 0;
          row.amount = Number((totalMinutes * row.rate)?.toFixed(2));
          row.softFlag = false;
          row.balanceTitle = `Balance(${capitalizeFirstLetter(applicableTier?.period_type ?? "")})`;
          row.balance = calculateTierBalance(applicableTier, totalMinutes);
          row.hardFlag = calculateTierHardFlag(applicableTier, row.balance);
          row.maxHours = applicableTier?.max_thresold
            ? `${applicableTier?.max_thresold}h`
            : "";
          row.annualCap = applicableTier?.annual_cap
            ? `${applicableTier?.annual_cap}h`
            : "";
          row.rateStr = `Rate: $ ${applicableTier?.rate?.toFixed(2)}${getRateUnitSuffix(firstContract.thresold_unit_type)}`;
          // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
          timesheetListData.push({
            ...periodTimesheets[0],
            contract_mstr_key: selectedContractMstrKey,
            budget: row.amount,
            ts_date: row.date,
            hours: Number(row.hours),
            minutes: Number(row.minutes),
            shifts: row.shifts,
            amount: row.amount,
          });
        }
      }
    }

    // Model: Weekend-Pay: is_transactional = 1, rate_type = "weekend-pay"
    // Logic: If 2+ weekend days in period, all weekend hours get Tier 2 rate.
    else if (
      firstContract.is_transactional === 1 &&
      firstContract.rate_type === "weekend-pay"
    ) {
      let contractTiers = contractData.filter(
        item =>
          item.contract_mstr_key === firstContract.contract_mstr_key &&
          item.contract_name_mstr_key === firstContract.contract_name_mstr_key,
      );
      const isWeekend = (date) => {
        const day = moment(date).day();
        return day === 0 || day === 6;
      };

      // check if current entry is a weekend day
      const isCurrentWeekend = isWeekend(moment(row.date).toDate());

      const existingWeekendTimesheets = periodTimesheets.filter(ts => {
        const dateInAmericanTZ = moment.tz(
          ts?.ts_date,
          DEFAULT_DATE_FORMAT,
          TIME_ZONE,
        );

        const parsedDate = dateInAmericanTZ.tz(moment.tz.guess()).toDate();
        return isWeekend(parsedDate);
      });

      const existingWeekendCount = existingWeekendTimesheets?.length || 0;

      // Total weekend days including current entry (if it's a weekend)
      const totalWeekendDays =
        existingWeekendCount + (isCurrentWeekend ? 1 : 0);

      // Weekend-pay rule: 2+ weekend timesheets triggers Tier 2 for ALL weekend entries
      const shouldApplyWeekendPayTier2 = totalWeekendDays >= 2;

      if (firstContract?.thresold_unit_type === "days") {
        // Group timesheets by date to find distinct work dates
        const dateGroups = {};

        periodTimesheets.forEach(ts => {
          const dateKey = moment
            .tz(ts.ts_date, DEFAULT_DATE_FORMAT, TIME_ZONE)
            .tz(moment.tz.guess())
            .format("YYYY-MM-DD");
          if (!dateGroups[dateKey]) {
            dateGroups[dateKey] = [];
          }
          dateGroups[dateKey].push(ts);
        });
        // Add the current service date to the groups
        const serviceDateStr = moment(row.date).format("YYYY-MM-DD");
        if (!dateGroups[serviceDateStr]) {
          dateGroups[serviceDateStr] = [];
        }
        // Sort distinct dates chronologically to assign day_index
        const distinctDates = Object.keys(dateGroups).sort();
        const dayIndexMap = {};
        distinctDates.forEach((date, index) => {
          dayIndexMap[date] = index + 1; // 1-based indexing
        });

        dayIndex = dayIndexMap[serviceDateStr];

        applicableTier = null;

        for (const tier of contractTiers) {
          const minThreshold = tier.min_thresold || 0;
          const maxThreshold = tier.max_thresold || Infinity;

          if (dayIndex >= minThreshold && dayIndex <= maxThreshold) {
            applicableTier = tier;
            break;
          }
        }
        if (!applicableTier && contractTiers.length > 0) {
          applicableTier = contractTiers[contractTiers.length - 1];
        }
        row.rate = applicableTier?.rate || 0;
        row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));

        // Apply weekend-pay override: if 2+ weekend days AND current is weekend, use Tier 2
        if (shouldApplyWeekendPayTier2 && isCurrentWeekend) {
          const tier2 =
            contractTiers.length > 1
              ? contractTiers[1]
              : contractTiers[contractTiers.length - 1];
          if (tier2) {
            applicableTier = tier2;
            row.rate = tier2.rate || 0;
            row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));
          }
        }
      } else if (isHoursStyleUnit(firstContract.thresold_unit_type)) {
        const sortedTiers = [...contractTiers].sort(
          (a, b) => (a.min_thresold || 0) - (b.min_thresold || 0),
        );

        const sortedTimesheets = periodTimesheets
          .filter(ts => {
            const dateInAmericanTZ = moment.tz(
              ts?.ts_date,
              DEFAULT_DATE_FORMAT,
              TIME_ZONE,
            );

            const parsedDate = dateInAmericanTZ.tz(moment.tz.guess()).toDate();
            return parsedDate <= moment(row.date).toDate();
          })
          .sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );

        let cumulativeMinutes = sortedTimesheets.reduce(
          (sum, ts) =>
            sum + getEntryQuantity(ts, firstContract.thresold_unit_type) * 60,
          0,
        );

        let finalCumulativeMinutes = (cumulativeMinutes || 0) + totalMinutes;

        applicableTier = null;

        for (const tier of sortedTiers) {
          if (finalCumulativeMinutes > (tier.min_thresold * 60 || 0)) {
            applicableTier = tier;
          }
        }
        row.rate = applicableTier?.rate || 0;
        row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));

        if (shouldApplyWeekendPayTier2 && isCurrentWeekend) {
          const tier2 =
            sortedTiers.length > 1
              ? sortedTiers[1]
              : sortedTiers[sortedTiers.length - 1];
          if (tier2) {
            applicableTier = tier2;
            row.rate = tier2.rate || 0;
            row.amount = Number((totalMinutes * (row.rate / 60))?.toFixed(2));
          }
        }
      }
      row.softFlag = false;
      row.balanceTitle = `Balance(${capitalizeFirstLetter(applicableTier?.period_type ?? "")})`;
      row.balance = calculateTierBalance(applicableTier, totalMinutes);
      row.hardFlag = calculateTierHardFlag(applicableTier, row.balance);
      row.maxHours = applicableTier?.max_thresold
        ? `${applicableTier?.max_thresold}h`
        : "";
      row.annualCap = applicableTier?.annual_cap
        ? `${applicableTier?.annual_cap}h`
        : "";
      row.rateStr = `Rate: $ ${applicableTier?.rate?.toFixed(2)}${getRateUnitSuffix(firstContract.thresold_unit_type)}`;
      // AS we are doing in for loop so for next timesheet we should avoid to have budget that's why we are entering in array first entry.
      timesheetListData.push({
        ...periodTimesheets[0],
        contract_mstr_key: selectedContractMstrKey,
        budget: row.amount,
        ts_date: row.date,
        hours: Number(row.hours),
        minutes: Number(row.minutes),
        shifts: row.shifts,
        amount: row.amount,
      });
    }
  });

  return {
    rowsData: rowData,
    multiTierStr,
  };
};

/*
 * NOT PORTED: mobile index.ts:872-1165 - the calculatePeriodBudgets() closure
 * that runs at the end of each row iteration and its `budgetComparisons`
 * output. On mobile that output is consumed after submit
 * (screens/app/taskManagement/AddPayRequestNew.tsx:741) to re-price the other
 * timesheets in the period, including the rule where a paying STEMI entry
 * zeroes out every gen-cardio entry on the same date. The web app has no
 * equivalent post-submit step, so the block is intentionally absent and the
 * return above carries two keys instead of three. This is the only place the
 * two files diverge in substance; if web ever needs period re-pricing, port
 * that range verbatim and re-add the third return key.
 */
